import { useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useShowCauseBackerInfo, useShowConsumerCommunicationStatus } from '@givebacks/api/backer';
import { useBreakPoints } from 'core';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, ButtonMantine, Container, Grid, Group, Stack, Text } from 'ui';
import { IconStar } from 'ui/Icons';

import { useNavigateToAuth } from '../../hooks/useNavigateToAuth/useNavigateToAuth';
import { CauseCard } from './components/CauseCard';
import { CommunicationLists } from './components/CommunicationLists';
import { NotAvailable } from './components/NotAvailable';

const CommunicationStatusContent = ({
  causeId,
  status,
  token,
}: {
  causeId?: string;
  status?: string;
  token?: string;
}) => {
  const { isAuthenticated } = useAuthentication();
  const { handleNavigateToAuth } = useNavigateToAuth();
  const { isSmallScreen } = useBreakPoints();

  const signIn = () => {
    handleNavigateToAuth({ page: 'sign-in' });
  };

  return (
    <Stack>
      <CauseCard causeId={causeId} status={status} token={token} fullWidth={isSmallScreen} />

      {status === 'unsubscribed' && (
        <Alert.Collapse
          title="You must be signed in to join communication list."
          icon={<IconStar />}
          in={!isAuthenticated}
        >
          <Group justify="space-between" align="top">
            <Text fz={12} fw={400} c="gray.8">
              Sign in or create an account to join a communication list
            </Text>
            <ButtonMantine variant={'outline'} size="sm" onClick={signIn}>
              Sign in
            </ButtonMantine>
          </Group>
        </Alert.Collapse>
      )}
    </Stack>
  );
};

const CommunicationStatusStack = ({
  causeId,
  status,
  token,
}: {
  causeId?: string;
  status?: string;
  token?: string;
}) => {
  return (
    <Stack gap={'xl'}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }} offset={{ base: 0, md: 2 }}>
          <CommunicationStatusContent causeId={causeId} status={status} token={token} />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }} offset={{ base: 0, md: 3 }}>
          <CommunicationLists causeId={causeId} token={token ?? ''} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
const CommunicationStatus = () => {
  const [search] = useSearchParams();
  const token = search.get('token');
  const { causeId } = useParams();

  const { data, isError } = useShowConsumerCommunicationStatus({ causeId, token });

  const { data: cause } = useShowCauseBackerInfo(causeId);
  const isSiteJoinsEnabled = cause?.site_joins_enabled;

  const causeUnAvailable = useMemo(() => {
    if (!isSiteJoinsEnabled && data?.communication_status !== 'subscribed') {
      return true;
    }
    return false;
  }, [isSiteJoinsEnabled, data?.communication_status]);

  return (
    <Container size="xl">
      {isError || causeUnAvailable ? (
        <NotAvailable />
      ) : (
        <CommunicationStatusStack
          causeId={data?.cause_id}
          status={data?.communication_status}
          token={token ?? ''}
        />
      )}
    </Container>
  );
};

export { CommunicationStatus };
